import { getTemplateSearchBoxStyles } from './SearchBox.styles'
import type { SearchBoxEvent, SearchBoxItem } from './types/SearchBox.type'
import { tryLoadDependencies, tryLoadLightDOMStyles, WebAwesomeComponent } from '../Global/loadDependency'
import { getBootstrapIconUrl, BootstrapIcon } from '../Global/icons'

const svgSearch = getBootstrapIconUrl(BootstrapIcon.Search)
const svgChevronDown = getBootstrapIconUrl(BootstrapIcon.ChevronDownIcon)
const t = document.createElement('template')
t.innerHTML = `
<div class="searchbox-container">
  <div class="input-wrapper" role="combobox" aria-expanded="false" aria-owns="searchbox-listbox" aria-haspopup="listbox">
    <wa-input
      with-clear
      exportparts="base,input"
      class="search-input"
      autocomplete="off"
      aria-autocomplete="list"
      aria-controls="searchbox-listbox"
      aria-activedescendant=""><wa-icon slot="start" src="${svgSearch}"></wa-icon><wa-icon slot="end" src="${svgChevronDown}"></wa-icon>
    </wa-input>
  </div>
  
  <div id="search-status" class="visually-hidden" aria-live="polite" aria-atomic="true"></div>
  
  <ul id="searchbox-listbox" class="suggestions-list" role="listbox" aria-label="Search results">
  </ul>
</div>
`
const TemplateSearchBox = t

class SearchBox extends HTMLElement {
    private _items: SearchBoxItem[] = []
    private _placeholder: string = 'Search...'
    private _minChars: number = 2
    private _maxResults: number = 10
    private _callback: ((e: SearchBoxEvent) => void) | null = null
    private _debounceTimer: number | null = null
    private _debounceMs: number = 300;
    private _boundDocumentClick?: (e: MouseEvent) => void; private _highlightedIndex: number = -1
    private _isLoading: boolean = false
    private _showSuggestions: boolean = false

    constructor() {
        super()

        tryLoadDependencies(document, [WebAwesomeComponent.Input, WebAwesomeComponent.Icon])

        const shadow = this.attachShadow({ mode: 'open' })
        shadow.appendChild(getTemplateSearchBoxStyles().content.cloneNode(true))
        shadow.appendChild(TemplateSearchBox.content.cloneNode(true))
        tryLoadLightDOMStyles('SearchBox.css')
    }

    static get observedAttributes() {
        return ['placeholder', 'min-chars', 'max-results', 'items']
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        switch (name) {
            case 'placeholder':
                this._placeholder = newValue || 'Search...'
                this.updatePlaceholder()
                break
            case 'min-chars':
                this._minChars = parseInt(newValue) || 2
                break
            case 'max-results':
                this._maxResults = parseInt(newValue) || 10
                break
            case 'items':
                this.updateItems(newValue)
                break
        }
    }

    connectedCallback() {
        this.updatePlaceholder()

        this._boundDocumentClick = this.handleDocumentClick.bind(this)
        document.addEventListener('click', this._boundDocumentClick)

        this.setupEventListeners()
        this.setupChevronClick()
    }



    disconnectedCallback() {
        if (this._boundDocumentClick) {
            document.removeEventListener('click', this._boundDocumentClick)
        }

        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer)
        }
    }


    // Public methods

    set callback(callback: (e: SearchBoxEvent) => void) {
        document.AscendAwesome?.LogManager?.logWarning('callback is deprecated. See docs.')
        this._callback = callback
    }

    set items(items: SearchBoxItem[]) {
        this._items = items || []
        // Don't automatically show suggestions when items are set
        // Let the user input trigger the search
    }

    get value(): string {
        const input = this.shadowRoot?.querySelector('wa-input') as any
        return input?.value || ''
    }

    set value(val: string) {
        const input = this.shadowRoot?.querySelector('wa-input') as any
        if (input) {
            input.value = val
        }
    }

    clear(): void {
        this.value = ''
        this.hideSuggestions()

        const eventData = { actionType: 'clear' as const }

        if (this._callback) {
            this._callback(eventData)
        }

        this.dispatchEvent(
            new CustomEvent('search-clear', {
                detail: eventData,
                bubbles: true,
                composed: true,
            }),
        )
    }

    focus(): void {
        const input = this.shadowRoot?.querySelector('wa-input') as any
        input?.focus()
    }

    showResults(items: SearchBoxItem[], searchValue?: string): void {
        this._isLoading = false
        const currentValue = searchValue ?? this.value.trim()
        this.displaySuggestions(items, currentValue)
    }
    // Private methods
    private filterItems(query: string): SearchBoxItem[] {
        if (!this._items.length) return []

        // Filter data based on query
        const results = this._items.filter(item =>
            item.description.toLowerCase().includes(query.toLowerCase())
        )

        // Sort results alphabetically by description
        results.sort((a, b) => a.description.localeCompare(b.description))

        // Limit results
        return results.slice(0, this._maxResults)
    }

    private setupEventListeners(): void {
        const input = this.shadowRoot?.querySelector('wa-input') as any

        if (input) {
            input.addEventListener('input', this.handleInput.bind(this))
            input.addEventListener('keydown', this.handleKeyDown.bind(this))
            input.addEventListener('focus', this.handleFocus.bind(this))
            input.addEventListener('blur', this.handleBlur.bind(this))
        }
    }
    private setupChevronClick(): void {
        const chevronIcon = this.shadowRoot?.querySelector('wa-icon[slot="end"]') as HTMLElement

        if (!chevronIcon) return

        chevronIcon.style.cursor = 'pointer'

        const handler = (e: Event) => {
            e.stopPropagation()
            this.toggleDropdown()
        }

        chevronIcon.addEventListener('click', handler)
    }


    private toggleDropdown(): void {
        const inputValue = (this.shadowRoot?.querySelector('wa-input') as any)?.value?.trim() || '';
        if (this._showSuggestions) return void this.hideSuggestions();

        const eventData = { actionType: 'search' as const, value: inputValue };
        this._callback?.(eventData);
        this.dispatchEvent(new CustomEvent('search-input', { detail: eventData, bubbles: true, composed: true }));

        if (!this._items.length) return;
        const results = inputValue.length >= this._minChars
            ? this.filterItems(inputValue)
            : this._items.slice(0, this._maxResults);
        this.displaySuggestions(results, inputValue.length >= this._minChars ? inputValue : '');
    }
    private updatePlaceholder(): void {
        const input = this.shadowRoot?.querySelector('wa-input') as any
        if (input) {
            input.placeholder = this._placeholder
        }
    }

    private updateItems(itemsJson: string): void {
        try {
            this._items = itemsJson ? JSON.parse(itemsJson) : []
        } catch (e) {
            console.error('Invalid items JSON:', e)
            this._items = []
        }
    }

    private handleInput(): void {
        const value = this.value.trim()

        // Clear previous debounce timer
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer)
        }

        // Reset highlight
        this._highlightedIndex = -1

        if (value.length >= this._minChars) {
            // Debounce to avoid excessive events
            this._debounceTimer = window.setTimeout(() => {
                const eventData = { actionType: 'search' as const, value }

                if (this._callback) {
                    this._callback(eventData)
                }

                this.dispatchEvent(
                    new CustomEvent('search-input', {
                        detail: eventData,
                        bubbles: true,
                        composed: true,
                    }),
                )

                // If items are already provided, filter and display them immediately
                if (this._items.length > 0) {
                    const results = this.filterItems(value)
                    this.displaySuggestions(results, value)
                }
            }, this._debounceMs)
        } else {
            this.hideSuggestions()
        }
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Tab') {
            this.hideSuggestions()
            return
        }

        // Everything below is ONLY for suggestions navigation
        if (!this._showSuggestions) {
            return
        }

        const suggestionsList = this.shadowRoot?.querySelector('.suggestions-list')
        const suggestions =
            suggestionsList?.querySelectorAll('.suggestion-item:not(.loading-item):not(.no-results)')

        if (!suggestions || suggestions.length === 0) {
            return
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this._highlightedIndex =
                    this._highlightedIndex < suggestions.length - 1
                        ? this._highlightedIndex + 1
                        : 0
                this.updateHighlight()
                break

            case 'ArrowUp':
                event.preventDefault()
                this._highlightedIndex =
                    this._highlightedIndex > 0
                        ? this._highlightedIndex - 1
                        : suggestions.length - 1
                this.updateHighlight()
                break

            case 'Enter':
                event.preventDefault()

                if (this._highlightedIndex >= 0) {
                    const highlightedItem = suggestions[this._highlightedIndex] as HTMLElement
                    const itemId = highlightedItem.dataset.id
                    const itemDescription = highlightedItem.dataset.description
                    if (itemId && itemDescription) {
                        this.selectItem({ id: itemId, description: itemDescription })
                    }
                }
                break



            case 'Escape':
                event.preventDefault()
                this.hideSuggestions()
                break
        }
    }


    private handleFocus(): void {
        this.setAttribute("focused", "")

        const value = this.value.trim();
        if (!value) {
            this.hideSuggestions();
            return;
        }

        if (value.length >= this._minChars && !this._showSuggestions) {
            this.showSuggestions();
        }
    }

    private handleBlur(): void {
        this.removeAttribute("focused");
        // Small delay to allow for click selection
        setTimeout(() => {
            this.hideSuggestions()
        }, 150)
    }

    private handleDocumentClick(event: MouseEvent): void {
        const target = event.target as Node


        if (this.contains(target) || this.shadowRoot?.contains(target)) {
            return
        }

        this.hideSuggestions()
    }


    private displaySuggestions(items: SearchBoxItem[], searchValue: string): void {
        const suggestionsList = this.shadowRoot?.querySelector('.suggestions-list')
        const statusDiv = this.shadowRoot?.querySelector('#search-status')

        if (!suggestionsList || !statusDiv) return

        suggestionsList.innerHTML = ''

        if (this._isLoading) {
            const loadingItem = document.createElement('li')
            loadingItem.className = 'suggestion-item loading-item'
            loadingItem.textContent = 'Searching...'
            loadingItem.setAttribute('role', 'status')
            loadingItem.setAttribute('aria-live', 'polite')
            suggestionsList.appendChild(loadingItem)

            statusDiv.textContent = 'Searching...'
        } else if (items.length === 0) {
            const noResultsItem = document.createElement('li')
            noResultsItem.className = 'suggestion-item no-results'
            noResultsItem.textContent = 'No results found'
            noResultsItem.setAttribute('role', 'option')
            suggestionsList.appendChild(noResultsItem)

            statusDiv.textContent = 'No results found'
        } else {
            items.forEach((item, index) => {
                const li = document.createElement('li')
                li.className = 'suggestion-item'
                li.setAttribute('role', 'option')
                li.setAttribute('id', `searchbox-option-${index}`)
                li.dataset.id = item.id.toString()
                li.dataset.description = item.description

                // Highlight matching text
                const highlightedText = this.highlightMatches(item.description, searchValue)
                li.innerHTML = highlightedText

                li.addEventListener('mousedown', () => {
                    this.selectItem(item)
                })

                li.addEventListener('mouseenter', () => {
                    this._highlightedIndex = index
                    this.updateHighlight()
                })

                suggestionsList.appendChild(li)
            })

            statusDiv.textContent = `${items.length} result${items.length === 1 ? '' : 's'} available`
        }

        this.showSuggestions()
    }

    private highlightMatches(text: string, searchValue: string): string {
        if (!searchValue) return text

        const escapedSearch = searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`(${escapedSearch})`, 'ig')
        return text.replace(regex, '<strong>$1</strong>')
    }

    private showSuggestions(): void {
        const suggestionsList = this.shadowRoot?.querySelector('.suggestions-list')
        const combobox = this.shadowRoot?.querySelector('.input-wrapper')

        if (suggestionsList && combobox) {
            suggestionsList.classList.add('show')
            combobox.setAttribute('aria-expanded', 'true')
            this._showSuggestions = true
        }
    }

    private hideSuggestions(): void {
        const suggestionsList = this.shadowRoot?.querySelector('.suggestions-list')
        const combobox = this.shadowRoot?.querySelector('.input-wrapper')
        const input = this.shadowRoot?.querySelector('wa-input') as any

        if (suggestionsList && combobox && input) {
            suggestionsList.classList.remove('show')
            combobox.setAttribute('aria-expanded', 'false')
            input.setAttribute('aria-activedescendant', '')
            this._showSuggestions = false
            this._highlightedIndex = -1
        }
    }

    private updateHighlight(): void {
        const suggestions = this.shadowRoot?.querySelectorAll(
            '.suggestion-item:not(.loading-item):not(.no-results)'
        )
        const input = this.shadowRoot?.querySelector('wa-input') as any

        if (!suggestions || !input) return

        suggestions.forEach((suggestion, index) => {
            suggestion.classList.toggle(
                'highlighted',
                index === this._highlightedIndex
            )
        })

        if (this._highlightedIndex >= 0 && suggestions[this._highlightedIndex]) {
            const activeOption = suggestions[this._highlightedIndex] as HTMLElement

            
            if (typeof activeOption.scrollIntoView === 'function') {
                activeOption.scrollIntoView({ block: 'nearest' })
            }

            input.setAttribute('aria-activedescendant', activeOption.id)
        } else {
            input.setAttribute('aria-activedescendant', '')
        }
    }


    private selectItem(item: SearchBoxItem): void {
        this.value = item.description
        this.hideSuggestions()

        const eventData = { actionType: 'select' as const, selectedItem: item, value: item.description }

        if (this._callback) {
            this._callback(eventData)
        }

        this.dispatchEvent(
            new CustomEvent('search-select', {
                detail: eventData,
                bubbles: true,
                composed: true,
            }),
        )
    }

    setLoading(loading: boolean): void {
        this._isLoading = loading
        if (loading) {
            this.displaySuggestions([], '')
        }
    }
}

// Only register if not already registered (prevents Storybook/HMR conflicts)
if (!customElements.get('ts-searchbox')) {
    customElements.define('ts-searchbox', SearchBox)
}

export { SearchBox }